/**
 * t293u: a task list as a calm table (desktop), like Issues (t276u). No pills: priority is a small dot and a word,
 * due is red only when overdue, Private is a lock and a word. Unread tasks (new to you, or changed or commented on by
 * someone else since you last opened them) have their title in bold. Done tasks are muted. Headers sort through the
 * same keys as the Sort select, so the two always agree.
 * t333u: a task with subtasks has a chevron before its title; it expands the subtasks inline as compact rows (tick to
 * complete, click to open the subtask's own sidebar). Expanded rows are kept for the session; subtasks load in one
 * batched request (Expand all included).
 */
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Fragment } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Bell, ChevronRight, Lock, MessageSquare, MessagesSquare, Paperclip, Repeat, Users, Lightbulb } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { useSetTaskDone, useSubtasks, useExpandedTasks, qk } from '@tradeoutlet/data';
import type { SubtaskItem } from '@tradeoutlet/shared';
import { recurrenceText } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useMembers, useTeams, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import {
  PRIORITY_DOT, PRIORITY_LABEL, reminderText, relatesTo, taskDueText, taskSortState, nextTaskSort,
  type RelatesPart, type TaskColumn, type TaskRowData, type TaskSort,
} from './taskList';
import { ukToday } from './privateTasks';

function SortHeader({ col, label, sort, onSort, className, extra }: { col: TaskColumn; label: string; sort: TaskSort; onSort: (s: TaskSort) => void; className?: string; extra?: React.ReactNode }) {
  const state = taskSortState(col, sort);
  return (
    <th scope="col" aria-sort={state} className={clsx('whitespace-nowrap px-3 py-2 font-semibold', className)}>
      <button type="button" onClick={() => onSort(nextTaskSort(col, sort))} className="inline-flex items-center gap-1 uppercase hover:text-slate-900 dark:hover:text-white">
        {label}{state === 'ascending' ? <ArrowUp className="h-3 w-3" /> : state === 'descending' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
      {extra}
    </th>
  );
}

const ICON = { lock: Lock, chat: MessagesSquare, team: Users, feedback: Lightbulb } as const;

export function RelatesTo({ parts }: { parts: RelatesPart[] }) {
  if (!parts.length) return <span className="text-slate-400">—</span>;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="task-relates">
      {parts.map(p => {
        const Icon = p.icon && p.icon !== 'dot' ? ICON[p.icon] : null;
        const body = (
          <>
            {p.icon === 'dot' && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p.color ?? '#94a3b8' }} aria-hidden />}
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            <span className="truncate">{p.text}</span>
          </>
        );
        return p.href
          ? <Link key={p.key} to={p.href} title={p.title} onClick={e => e.stopPropagation()} className="inline-flex min-w-0 items-center gap-1 hover:text-slate-900 hover:underline dark:hover:text-white" data-relates={p.key.split(':')[0]}>{body}</Link>
          : <span key={p.key} title={p.title} className="inline-flex min-w-0 items-center gap-1" data-relates={p.key.split(':')[0]}>{body}</span>;
      })}
    </span>
  );
}

function Count({ icon: Icon, n, label }: { icon: typeof MessageSquare; n: string | number; label: string }) {
  return <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-slate-400" aria-label={label} title={label}><Icon className="h-3 w-3" aria-hidden />{n}</span>;
}

function Assignees({ ids }: { ids: string[] }) {
  const { byId } = useMembers();
  const openPerson = useUi(s => s.openPerson);
  if (!ids.length) return <span className="text-slate-400">—</span>;
  const first = byId.get(ids[0]);
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="flex shrink-0 -space-x-1.5">
        {ids.slice(0, 3).map(id => <span key={id} className="rounded-full ring-2 ring-white dark:ring-slate-900"><Avatar size={20} name={byId.get(id)?.display_name} id={id} url={byId.get(id)?.avatar_url} /></span>)}
      </span>
      <button type="button" onClick={e => { e.stopPropagation(); openPerson(ids[0]); }} className="truncate text-left hover:underline" data-testid="task-assignee">
        {first?.display_name ?? 'Someone'}
      </button>
      {ids.length > 1 && <span className="shrink-0 text-xs text-slate-500">+{ids.length - 1}</span>}
    </span>
  );
}

/** t333u: the chevron before a title (a same-width gap when there are no subtasks, so titles line up). */
function ExpandChevron({ t, open, onToggle }: { t: TaskRowData; open: boolean; onToggle: () => void }) {
  if (!t.subtask_count) return <span className="w-4 shrink-0" aria-hidden />;
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onToggle(); }} aria-expanded={open} data-testid="task-expand"
      aria-label={`${open ? 'Hide' : 'Show'} ${t.subtask_count} subtask${t.subtask_count === 1 ? '' : 's'} of ${t.title}`}
      className="-ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200">
      <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden />
    </button>
  );
}

/** t333u: the expanded subtasks of one task: compact, indented rows under it. */
function SubtaskRows({ parent, today, onOpen }: { parent: TaskRowData; today: string; onOpen: (t: { id: string; title: string }) => void }) {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const q = useSubtasks(ws, parent.id);
  const setDone = useSetTaskDone(ws);
  const tick = (s: SubtaskItem) => setDone.mutateAsync({ task: s, done: !s.completed_at }).then(
    () => void qc.invalidateQueries({ queryKey: qk.tasks(parent.project_id) }),
    e => toast(errorMessage(e), 'error'),
  );
  const note = (text: string, tone = 'text-slate-500') => <tr data-testid="subtask-note"><td /><td colSpan={6} className={clsx('py-1.5 pl-[3.25rem] pr-3 text-xs', tone)}>{text}</td></tr>;
  if (q.isLoading) return note('Loading subtasks…');
  if (q.error) return note(errorMessage(q.error), 'text-rose-700 dark:text-rose-400');
  if (!q.data?.length) return note('No subtasks you can see.');
  return (
    <>
      {q.data.map(s => {
        const done = Boolean(s.completed_at);
        const due = taskDueText(s, today);
        const tone = done ? 'text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300';
        return (
          <tr key={s.id} data-testid="subtask-row" data-parent={parent.id} data-done={done ? 'true' : undefined} onClick={() => onOpen(s)}
            className="cursor-pointer bg-slate-50/50 text-[13px] hover:bg-slate-100/70 dark:bg-slate-800/20 dark:hover:bg-slate-800/50">
            <td />
            <td className="min-w-0 py-1.5 pl-[3.25rem] pr-3">
              <div className="flex min-w-0 items-center gap-2">
                <input type="checkbox" checked={done} onClick={e => e.stopPropagation()} onChange={() => void tick(s)} disabled={setDone.isPending && setDone.variables?.task.id === s.id}
                  aria-label={done ? `Reopen ${s.title}` : `Mark as done: ${s.title}`} data-testid="subtask-check" />
                <span className={clsx('truncate', done ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-800 dark:text-slate-200')} data-testid="subtask-title">{s.title}</span>
              </div>
            </td>
            <td />
            <td className={clsx('min-w-0 px-3 py-1.5', tone)}><Assignees ids={s.assignee_ids ?? []} /></td>
            <td className="whitespace-nowrap px-3 py-1.5">{due.text && <span className={due.overdue && !done ? 'text-rose-700 dark:text-rose-400' : tone}>{due.text}</span>}</td>
            <td />
            <td className={clsx('whitespace-nowrap px-3 py-1.5', tone)}>{s.status_name}</td>
          </tr>
        );
      })}
    </>
  );
}

export function TasksTable({ rows, groups, sort, onSort, selected, onSelect, onOpen, inProject, milestones, statusCell, testId = 'tasks-table', today = ukToday() }: {
  rows: TaskRowData[];
  /** Project list: rows under status headings (board order) instead of one flat list. */
  groups?: { id: string; header: React.ReactNode; rows: TaskRowData[]; collapsed?: boolean; footer?: React.ReactNode }[];
  /** Project list: milestone names, shown in Relates to. */
  milestones?: Map<string, string>; sort: TaskSort; onSort: (s: TaskSort) => void;
  selected: Set<string>; onSelect: (next: Set<string>) => void; onOpen: (t: { id: string; title: string }) => void;
  /** Inside a project: no project name in Relates to. */
  inProject?: boolean;
  /** The project list's status select; otherwise the status name. */
  statusCell?: (t: TaskRowData) => React.ReactNode;
  testId?: string; today?: string;
}) {
  const { byId: teams } = useTeams();
  const all = groups ? groups.flatMap(g => g.rows) : rows;
  const allOn = all.length > 0 && all.every(r => selected.has(r.id));
  const expanded = useExpandedTasks();
  const parents = all.filter(r => r.subtask_count > 0);
  const allOpen = parents.length > 0 && parents.every(r => expanded.isOpen(r.id));
  const toggle = (id: string) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); onSelect(n); };
  const renderRow = (t: TaskRowData) => {
    const done = Boolean(t.completed_at);
    const due = taskDueText(t, today);
    const remind = reminderText(t);
    const strong = t.unread && !done;
    const tone = done ? 'text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300';
    const open = t.subtask_count > 0 && expanded.isOpen(t.id);
    return (
      <Fragment key={t.id}>
      <tr data-testid="task-row" data-unread={t.unread ? 'true' : undefined} data-done={done ? 'true' : undefined} onClick={() => onOpen(t)}
        className={clsx('cursor-pointer align-top hover:bg-slate-50 dark:hover:bg-slate-800/50', selected.has(t.id) && 'bg-slate-50 dark:bg-slate-800/50', done && 'text-slate-400 dark:text-slate-500')}>
        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Select ${t.title}`} checked={selected.has(t.id)} onChange={() => toggle(t.id)} />
        </td>
        <td className="min-w-0 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <ExpandChevron t={t} open={open} onToggle={() => expanded.toggle(t.id)} />
            <button type="button" onClick={e => { e.stopPropagation(); onOpen(t); }} data-testid="task-title"
              className={clsx('truncate text-left hover:underline', strong ? 'font-semibold text-slate-900 dark:text-white' : done ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-800 dark:text-slate-200')}>{t.title}</button>
            {strong && <span className="sr-only">(unread)</span>}
            {t.subtask_count > 0 && <span className="shrink-0 text-xs tabular-nums text-slate-400" title={`${t.subtask_done_count} of ${t.subtask_count} subtasks done`} aria-label={`${t.subtask_done_count} of ${t.subtask_count} subtasks done`} data-testid="task-subtask-count">{t.subtask_done_count}/{t.subtask_count}</span>}
            {t.comment_count > 0 && <Count icon={MessageSquare} n={t.comment_count} label={`${t.comment_count} comment${t.comment_count === 1 ? '' : 's'}`} />}
            {(t.attachment_count ?? 0) > 0 && <Count icon={Paperclip} n={t.attachment_count!} label={`${t.attachment_count} attachment${t.attachment_count === 1 ? '' : 's'}`} />}
            {remind && <span className="shrink-0 text-slate-400" title={remind} aria-label={remind} data-testid="task-reminder-icon"><Bell className="h-3 w-3" aria-hidden /></span>}
            {t.recurrence && <span className="shrink-0 text-slate-400" title={recurrenceText(t.recurrence, t.due_date)} aria-label="Repeats"><Repeat className="h-3 w-3" aria-hidden /></span>}
          </div>
          {(t.location_name || t.area_name) && <div className="truncate pl-[1.375rem] text-xs text-slate-500">{[t.location_name, t.area_name].filter(Boolean).join(' · ')}</div>}
        </td>
        <td className={clsx('min-w-0 px-3 py-2.5 text-xs', done ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400')}><RelatesTo parts={relatesTo(t, teams, { inProject, milestone: t.milestone_id ? milestones?.get(t.milestone_id) : undefined })} /></td>
        <td className={clsx('min-w-0 px-3 py-2.5', tone)}><Assignees ids={t.assignee_ids ?? []} /></td>
        <td className="px-3 py-2.5" data-testid="task-due" title={remind ?? undefined}>
          {due.text && <div className={clsx('whitespace-nowrap', due.overdue ? 'text-rose-700 dark:text-rose-400' : tone)}>{due.text}</div>}
          {remind && <div className="truncate text-xs text-slate-500">{remind}</div>}
        </td>
        <td className={clsx('whitespace-nowrap px-3 py-2.5', tone)} data-testid="task-priority">
          {t.priority !== 'none' && <><span className={clsx('mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle', PRIORITY_DOT[t.priority])} aria-hidden />{PRIORITY_LABEL[t.priority]}</>}
        </td>
        <td className={clsx('whitespace-nowrap px-3 py-2.5', tone)} onClick={statusCell ? e => e.stopPropagation() : undefined}>
          {statusCell ? statusCell(t) : (t.status_name ?? '')}
        </td>
      </tr>
      {open && <SubtaskRows parent={t} today={today} onOpen={onOpen} />}
      </Fragment>
    );
  };
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full min-w-[60rem] table-fixed text-sm" data-testid={testId}>
        <colgroup><col className="w-10" /><col /><col className="w-56" /><col className="w-44" /><col className="w-52" /><col className="w-24" /><col className="w-32" /></colgroup>
        <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
          <tr>
            <th className="px-3 py-2"><input type="checkbox" aria-label={`Select all shown (${all.length})`} checked={allOn} onChange={() => onSelect(allOn ? new Set() : new Set(all.map(r => r.id)))} /></th>
            <SortHeader col="title" label="Task" sort={sort} onSort={onSort} className="overflow-hidden text-ellipsis"
              extra={parents.length > 0 && <button type="button" onClick={() => expanded.set(parents.map(r => r.id), !allOpen)} data-testid="tasks-expand-all"
                className="ml-3 font-medium normal-case text-slate-500 hover:text-slate-900 hover:underline dark:hover:text-white">{allOpen ? 'Collapse all' : 'Expand all'}</button>} />
            <SortHeader col="relates" label="Relates to" sort={sort} onSort={onSort} />
            <th scope="col" className="px-3 py-2 font-semibold">Assignee</th>
            <SortHeader col="due" label="Due" sort={sort} onSort={onSort} />
            <SortHeader col="priority" label="Priority" sort={sort} onSort={onSort} />
            <SortHeader col="status" label="Status" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {groups ? null : rows.map(renderRow)}
        </tbody>
        {groups?.map(g => (
          <tbody key={g.id} className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="task-group">
            <tr><td colSpan={7} className="border-t border-slate-200 bg-slate-50/60 px-3 py-1.5 dark:border-slate-800 dark:bg-slate-800/40">{g.header}</td></tr>
            {!g.collapsed && g.rows.map(renderRow)}
            {!g.collapsed && g.footer && <tr><td colSpan={7} className="px-3 py-1.5">{g.footer}</td></tr>}
          </tbody>
        ))}
      </table>
    </div>
  );
}
